/** Shared flight itinerary helpers — mirror travel_app preview JS. */

export const pick = (obj, keys) => keys.reduce((out, key) => {
  if (obj && obj[key] !== undefined) out[key] = obj[key]
  return out
}, {})

export const FLIGHT_INFO_KEYS = [
  'airline_name', 'airline_code', 'icon_path', 'flight_number', 'aircraft', 'cabin', 'fare_class',
  'from_code', 'to_code', 'departure_label', 'arrival_label', 'departure_time', 'arrival_time',
  'departure_date', 'arrival_date', 'departure_date_label', 'arrival_date_label',
  'duration', 'stops_label', 'departure_terminal', 'arrival_terminal',
  'from_city', 'to_city', 'from_city_label', 'to_city_label',
  'from_airport_name', 'to_airport_name', 'route_label', 'route_type',
  'segments', 'connections', 'baggage',
]

export const pickFlightInfo = (raw) => {
  const info = pick(raw, FLIGHT_INFO_KEYS)
  const ret = raw?.return_flight_info
  if (ret && typeof ret === 'object') {
    info.return_flight_info = pick(ret, FLIGHT_INFO_KEYS)
  }
  return info
}

export const tripTypeOf = (bag) => String(
  bag?.FareType || bag?.trip_type || '',
).trim().toUpperCase()

export const isRoundTripBag = (bag) => {
  const trip = tripTypeOf(bag)
  if (trip === 'RT' || trip === 'RS') return true
  const ret = bag?.flight_info?.return_flight_info
  return Boolean(ret && typeof ret === 'object')
}

export const isRoundTripTrip = (trip) => Boolean(trip?.is_round_trip)
  || ['RT', 'RS'].includes(String(trip?.trip_type || '').toUpperCase())

/** Onward segments + return segments (RT). Connecting one-way stays on flight_info.segments. */
export const itinerarySegments = (flightInfo = {}) => {
  const onward = Array.isArray(flightInfo.segments) && flightInfo.segments.length
    ? flightInfo.segments
    : []
  const returnInfo = (
    flightInfo.return_flight_info && typeof flightInfo.return_flight_info === 'object'
  ) ? flightInfo.return_flight_info : null
  const returnSegs = returnInfo && Array.isArray(returnInfo.segments) && returnInfo.segments.length
    ? returnInfo.segments
    : []
  return returnSegs.length ? [...onward, ...returnSegs] : onward
}

export const legHops = (legInfo) => {
  const info = legInfo && typeof legInfo === 'object' ? legInfo : {}
  const segs = Array.isArray(info.segments) && info.segments.length
    ? info.segments
    : ((info.from_code || info.flight_number) ? [info] : [])
  return segs
}

export const splitBookingLegs = (booking = {}) => {
  const apiOnward = booking.onward && typeof booking.onward === 'object' ? booking.onward : null
  const apiReturn = booking.return && typeof booking.return === 'object' ? booking.return : null
  if (apiOnward || apiReturn) {
    return {
      isRt: Boolean(apiReturn),
      going: Array.isArray(apiOnward?.segments) && apiOnward.segments.length
        ? apiOnward.segments
        : legHops(apiOnward?.flight_info || {}),
      coming: Array.isArray(apiReturn?.segments) && apiReturn.segments.length
        ? apiReturn.segments
        : legHops(apiReturn?.flight_info || {}),
      info: apiOnward?.flight_info || booking.flight_info || {},
      returnInfo: apiReturn?.flight_info || null,
    }
  }
  const info = booking.flight_info && typeof booking.flight_info === 'object'
    ? booking.flight_info
    : {}
  const returnInfo = info.return_flight_info && typeof info.return_flight_info === 'object'
    ? info.return_flight_info
    : null
  const going = legHops(info)
  const coming = returnInfo ? legHops(returnInfo) : []
  const isRt = isRoundTripTrip(booking) || Boolean(returnInfo)
  if (!isRt) {
    const segments = Array.isArray(booking.segments) ? booking.segments : []
    return {
      isRt: false,
      going: going.length ? going : segments,
      coming: [],
      info,
      returnInfo: null,
    }
  }
  return { isRt, going, coming, info, returnInfo }
}

/** CreateItinerary / SeatLayout: Fare + Tax. SSRNetAmount 0 is missing, not free. */
export const seatCharge = (seat) => {
  const net = Number(seat?.SSRNetAmount ?? seat?.Charge)
  if (Number.isFinite(net) && net > 0) return net
  return (Number(seat?.Fare) || 0) + (Number(seat?.Tax) || 0)
}

export const addonChargesFromBag = (bag) => {
  const b = bag || {}
  const lines = Array.isArray(b.ssr_addon_lines) ? b.ssr_addon_lines : []
  if (lines.length) {
    return lines.reduce((sum, row) => sum + (Number(row.amount) || 0), 0)
  }
  const seats = Array.isArray(b.ssr_seats) ? b.ssr_seats : []
  const ssrAmount = Number(b.SSRAmount) || 0
  if (seats.length) {
    const seatFees = seats.reduce((sum, seat) => sum + seatCharge(seat), 0)
    return Math.max(0, ssrAmount - seatFees)
  }
  return ssrAmount
}

export const seatChargesFromBag = (bag, seatSelection = {}) => {
  const b = bag || {}
  if (b.seats_skipped) return 0
  const selected = Object.values(seatSelection || {})
  if (selected.length) {
    return selected.reduce((sum, seat) => sum + (Number(seat.price) || 0), 0)
  }
  const seats = Array.isArray(b.ssr_seats) ? b.ssr_seats : []
  return seats.reduce((sum, seat) => sum + seatCharge(seat), 0)
}

export const recomputeSsrAmount = (bag, seatSelection = {}) => {
  const b = bag || {}
  if (b.seats_skipped) {
    return addonChargesFromBag(b)
  }
  return addonChargesFromBag(b) + seatChargesFromBag(b, seatSelection)
}

/**
 * Map SeatLayout Trips/Journeys onto itinerary FUIDs.
 * Return legs often reuse 1..n per bound — offset them by onwardCount.
 */
export const flattenSeatLayoutSegments = (data, bag) => {
  const routeSegs = itinerarySegments(bag?.flight_info || {})
  const onwardCount = Array.isArray(bag?.flight_info?.segments)
    ? bag.flight_info.segments.length
    : 0
  const items = []
  let globalIndex = 0
  for (const trip of data?.Trips || []) {
    for (const journey of trip?.Journey || []) {
      for (const segment of journey?.Segments || []) {
        const routeSeg = routeSegs[globalIndex] || {}
        const fromLayout = Number(segment.FUID || segment.fuid || 0)
        const fromRoute = Number(routeSeg.fuid || 0)
        const isReturnSeg = onwardCount > 0 && globalIndex >= onwardCount
        let fuid = globalIndex + 1
        if (fromRoute > 0 && (!isReturnSeg || fromRoute > onwardCount)) {
          fuid = fromRoute
        } else if (fromLayout > 0 && !isReturnSeg) {
          fuid = fromLayout
        } else if (fromLayout > 0 && isReturnSeg) {
          fuid = onwardCount + fromLayout
        }
        const flightNo = String(segment.FlightNo || '').trim()
        const airline = String(segment.AirlineName || routeSeg.airline_name || '').trim()
        const route = [routeSeg.from_code, routeSeg.to_code].filter(Boolean).join(' → ')
        items.push({
          fuid,
          label: [
            airline,
            flightNo ? `Flight ${flightNo}` : '',
            route || routeSeg.label,
          ].filter(Boolean).join(' · ')
            || `${segment?.from_code || ''} → ${segment?.to_code || ''}`.trim()
            || `Segment ${globalIndex + 1}`,
          seats: Array.isArray(segment.Seats) ? segment.Seats : (segment.seats || []),
          raw: segment,
        })
        globalIndex += 1
      }
    }
  }
  return items
}

export const routeChip = (info) => {
  if (!info) return ''
  if (info.route_label) return info.route_label
  const from = info.from_code || info.departure_code
  const to = info.to_code || info.arrival_code
  return from && to ? `${from} → ${to}` : ''
}

/** IndiGo Stretch LTCP etc. — not a meal/bag; CreateItinerary Code 1111 if sold as SSR. */
const FARE_BUNDLE_CODES = new Set(['LTCP'])
const BUNDLE_TITLE_TOKENS = ['bundle', 'combo pack', 'lite combo']

export const isFareBundleSsr = (item) => {
  const code = String(item?.code || item?.selection?.code || '').trim().toUpperCase()
  if (FARE_BUNDLE_CODES.has(code)) return true
  const title = `${item?.title || ''} ${item?.description || ''}`.toLowerCase()
  return BUNDLE_TITLE_TOKENS.some((token) => title.includes(token))
}

export const SEAT_LAYOUT_UNAVAILABLE_CODE = 'seat_layout_unavailable'

export const parseSeatLayoutError = (err) => {
  const top = err?.response?.data || {}
  const body = top.data && typeof top.data === 'object' ? top.data : top
  const code = String(body.code || body.ui_code || '').trim()
  const message = String(top.message || body.message || '').trim()
  return { code, message, body }
}

export const isSeatLayoutUnavailableError = (err) => {
  const { code, body } = parseSeatLayoutError(err)
  return code === SEAT_LAYOUT_UNAVAILABLE_CODE || body.seat_layout_available === false
}

/** Map flattenSeatLayoutSegments output to seat page state (matches seats-preview renderMap input). */
export const mapSeatLayoutSegmentsForUi = (layoutData, bag) => {
  const onwardCount = Array.isArray(bag?.flight_info?.segments)
    ? bag.flight_info.segments.length
    : 0
  const returnCount = Array.isArray(bag?.flight_info?.return_flight_info?.segments)
    ? bag.flight_info.return_flight_info.segments.length
    : 0
  return flattenSeatLayoutSegments(layoutData, bag).map((segment, index) => {
    const isReturn = onwardCount > 0 && index >= onwardCount
    return {
      fuid: segment.fuid,
      label: segment.label,
      leg: isReturn ? 'return' : 'onward',
      legIndex: isReturn ? (index - onwardCount + 1) : (index + 1),
      legTotal: isReturn ? (returnCount || 1) : (onwardCount || 1),
      seats: segment.seats,
    }
  })
}
